import ScatterPlotViz from '../vizs/ScatterPlotViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * Used to display values for typically two variables for a set of data. If the points are coded (color/shape/size), one additional variable can be displayed. The data are displayed as a collection of points, each having the value of one variable determining the position on the horizontal axis and the value of the other variable determining the position on the vertical axis.
 *
 * ### Example Usage
 * ```javascript
 *
 *   import {ScatterPlot} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ScatterPlot($el, data, config);
 *
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 3 or 4 columns.
 *
 * Each row represents a data point.
 *
 * The first column should represent the id/label of the data point.
 *
 * The second and third columns should represent the X and Y coordinates of the point.
 *
 * The fourth column (optional) should represent the color group to which the point belongs.
 *
 * Note: the x and y coordinates can be numebers or strings, and will be sorted in the orther in which they appear in the table.
 *
 * | id   |  x    | y    |  color |
 * | ---- | ---   | ---- | -----  |
 * |  a   |  14.2 | 24.2 | group1 |
 * |  b   |  52.3 | 51.2 | group1 |
 * |  c   |  16.3 | 21.6 | group2 |
 * |  d   |  43.0 | 18.2 | group2 |
 *
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Se utiliza para mostrar valores para normalmente dos variables para un conjunto de datos. Si los puntos están codificados (color/forma/tamaño), se puede mostrar una variable adicional. Los datos se muestran como una colección de puntos, cada uno de los cuales tiene el valor de una variable que determina la posición en el eje horizontal y el valor de la otra variable que determina la posición en el eje vertical.
 *
 * ### Ejemplo de uso
 * ```javascript
 *
 *   import {ScatterPlot} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ScatterPlot($el, data, config);
 *
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 3 o 4 columnas.
 *
 * Cada fila representa un punto de datos.
 *
 * La primera columna debe representar la identificación/etiqueta del punto de datos.
 *
 * La segunda y tercera columnas deben representar las coordenadas X e Y del punto.
 *
 * La cuarta columna (opcional) debe representar el grupo de color al que pertenece el punto.
 *
 * Nota: las coordenadas x e y pueden ser números o cadenas, y se ordenarán en el orden en que aparecen en la tabla.
 *
 * | id   |  x    | y    |  color |
 * | ---- | ---   | ---- | -----  |
 * |  a   |  14.2 | 24.2 | group1 |
 * |  b   |  52.3 | 51.2 | group1 |
 * |  c   |  16.3 | 21.6 | group2 |
 * |  d   |  43.0 | 18.2 | group2 |
 *
 *
 * {.data-table}
 * */
export default class ScatterPlotComponent extends BaseComponent {
  static vizClass = ScatterPlotViz
  static dataFormat = [
    { index: 0, axis: '', label: 'id', types: [String], role: 'id', roleLabel: 'Item' },
    {
      index: 1,
      axis: 'x0',
      label: 'x value',
      types: [Number, Date],
      role: 'value',
      roleLabel: 'X value - Number or Date (dd-mm-yyyy)',
    },
    {
      index: 2,
      axis: 'y0',
      label: 'x value',
      types: [Number],
      role: 'value',
      roleLabel: 'Y value',
    },
    {
      index: 3,
      axis: '',
      label: 'color',
      types: [String, null],
      role: 'value',
      roleLabel: 'Category/Color',
      optional: true,
    },
  ]
  static componentLabel = 'Scatter plot'
  static componentLabelEs = 'Gráfico de dispersión'
  static defaultConfig = {
    title: this.componentLabel,
    TooltipBehavior: {
      axis: {
        x: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
        y: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
    },
  }
  static dataRequirements = {
    avoidDates: true,
  }
}
